<template>
  <div>
    <div
      v-if="
        !previewMode &&
        !isLoadingTemplate &&
        mjml &&
        mjml.children &&
        mjml.children[0] &&
        mjml.children[0].children
      "
      class="email"
      :style="{
        'margin-top':
          topBarStore.removeTopBar || !topBarStore.showMainTopBar
            ? '0'
            : '56px',
        'padding-top': topBarStore.showWindowBar ? '65px' : '30px',
        'padding-left': layoutStore.showControlPanelExpand ? '30px' : '20px',
        'padding-right': layoutStore.showControlPanelExpand ? '30px' : '20px',
        'padding-bottom': '110px',
      }"
      @paste="onPaste"
    >
      <div
        v-if="mjmlStore.isMobileFirstEnabled"
        :class="{ mobilePreviewWrapper: isMobileView }"
      >
        <div
          :class="{
            mobilePreview: isMobileView,
            desktopPreview: !isMobileView,
          }"
        >
          <div
            id="templateContent"
            class="relative"
            :style="{
              width: isMobileView ? '100%' : 'auto',
              backgroundColor: bgColor,
              backgroundImage: bgGradient,
            }"
          >
            <div
              v-if="mjmlStore.isMobileFirstEnabled"
              class="pointer-events-none absolute inset-y-0 left-4 right-4 z-0"
              :style="{
                boxShadow: '5px 5px 20px 5px rgba(0, 0, 0, 0.03)',
              }"
            ></div>
            <mjmlStyle></mjmlStyle>

            <mjmlFonts></mjmlFonts>

            <VueDraggable
              :model-value="sections"
              :animation="150"
              group="sections"
              handle=".movement-drag-handle"
              ghost-class="ghost"
              @update="onUpdate"
              @add="onAdd"
              @start="onStart"
              @end="onEnd"
            >
              <mjmlSection
                v-for="(section, index) in sections"
                :key="section.uid"
                :index="index as number"
              ></mjmlSection>
            </VueDraggable>
          </div>
        </div>
      </div>

      <div
        v-else
        id="templateContent"
        class="relative"
        :style="{
          backgroundColor: bgColor,
          backgroundImage: bgGradient,
        }"
      >
        <div
          v-if="mjmlStore.isMobileFirstEnabled"
          class="pointer-events-none absolute inset-y-0 left-4 right-4 z-0"
          :style="{
            boxShadow: '5px 5px 20px 5px rgba(0, 0, 0, 0.03)',
          }"
        ></div>
        <mjmlStyle></mjmlStyle>

        <mjmlFonts></mjmlFonts>

        <VueDraggable
          :model-value="mjml.children[0].children"
          :animation="150"
          group="sections"
          handle=".movement-drag-handle"
          @update="onUpdate"
          @add="onAdd"
          @start="onStart"
          @end="onEnd"
        >
          <mjmlSection
            v-for="(section, index) in mjml.children[0].children"
            :key="section.uid"
            :index="index as number"
          ></mjmlSection>
        </VueDraggable>
      </div>

      <RightPanel> </RightPanel>
      <add-block-dialog v-if="showBlockAddDialog"></add-block-dialog>
      <add-structure-dialog
        v-if="showStructureAddDialog"
      ></add-structure-dialog>
      <add-ai-text-dialog v-if="showAiTextDialog"></add-ai-text-dialog>
    </div>

    <renderPreview v-else-if="previewMode"></renderPreview>

    <t-loading
      v-if="isLoadingTemplate"
      data-testid="loading-template"
      inverse
    />
    <div
      v-else-if="generalStore.translationInProgress"
      class="fixed inset-0 z-[9999] bg-black/70"
    >
      <t-loading class="sticky top-0" data-testid="loading-template" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import mjmlStyle from "../mjml/mjmlStyle.vue";
  import mjmlFonts from "../mjml/mjmlFonts.vue";
  import mjmlSection from "../mjml/mjmlSection.vue";
  import renderPreview from "./renderPreview.vue";
  import RightPanel from "./RightPanel/RightPanel.vue";
  import addBlockDialog from "./dialogs/addBlockDialog.vue";
  import addStructureDialog from "./dialogs/addStructureDialog.vue";
  import addAiTextDialog from "./dialogs/addAiTextBlock.vue";
  import { resolve } from "./columnResolver";

  import { getCopiedElement } from "@topol/utils";

  import { useTopBarStore } from "@/stores/topBar";
  import { usePreviewStore } from "@/stores/preview";
  import { useDialogsStore } from "@/stores/dialogs";
  import { useDraggingStore } from "@/stores/draggingStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
  import { usePremadeBlocksStore } from "@/stores/premadeBlocksStore";
  import { useLayoutStore } from "@/stores/layoutStore";

  import { TLoading } from "@topol/twig-ui";
  import { VueDraggable } from "vue-draggable-plus";
  import { computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";

  const topBarStore = useTopBarStore();
  const previewStore = usePreviewStore();
  const dialogsStore = useDialogsStore();
  const draggingStore = useDraggingStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const savedBlocksStore = useSavedBlocksStore();
  const premadeBlocksStore = usePremadeBlocksStore();
  const layoutStore = useLayoutStore();

  const mjml = computed(() => {
    return mjmlStore.getMjml();
  });

  const bgColor = computed(() => {
    return mjmlStore.getBodyBg;
  });

  const bgGradient = computed(() => {
    return mjmlStore.getStyle("body>div", "background-image") || "";
  });

  const isLoadingTemplate = computed(() => {
    return generalStore.loadingTemplate;
  });

  const showBlockAddDialog = computed(() => {
    return dialogsStore.addBlockDialog.opened;
  });

  const showStructureAddDialog = computed(() => {
    return dialogsStore.addStructureDialog.opened;
  });

  const showAiTextDialog = computed(() => {
    return dialogsStore.addAiTextDialog.opened;
  });

  const previewMode = computed(() => {
    return previewStore.previewMode;
  });

  const isMobileView = computed(() => {
    return mjmlStore.isMobileView;
  });

  const sections = computed(() => mjmlStore.mjml.children?.[0]?.children ?? []);

  watch(
    () => sections.value.map((s: any) => s.uid).join(","),
    () => {
      nextTick(() => {
        window.$event.emit("reset-interact");
      });
    }
  );

  function onStart(evt: { oldIndex: number | undefined }) {
    // Save starting section and column for later reordering
    draggingStore.startDragging({
      sectionIndex: undefined,
      columnIndex: undefined,
      blockIndex: evt.oldIndex,
    });
  }

  function onEnd() {
    draggingStore.stopDragging();
  }

  function onUpdate(evt: {
    oldIndex: number | undefined;
    newIndex: number | undefined;
  }) {
    mjmlStore.reorderSections({
      sectionIndex: undefined,
      columnIndex: undefined,
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex,
    });
    // If selected set new position as selected
  }

  async function onAdd(evt: any) {
    const droppedItem = evt.item;
    // mjmlStore.mjml.children[0].children.splice(evt.newIndex, 1);

    if (evt.from.parentNode.id == "readyblocks") {
      // droppedItem.parentNode.removeChild(droppedItem);

      premadeBlocksStore.addPremadeBlock({
        sectionIndex: evt.newIndex,
        blockType: droppedItem.dataset.blocktype,
        blockIndex: parseInt(droppedItem.dataset.blockindex),
      });
    } else if (evt.from.parentNode.id == "saved") {
      // droppedItem.parentNode.removeChild(droppedItem);
      savedBlocksStore.addSavedBlock({
        sectionIndex: evt.newIndex,
        blockIndex: parseInt(droppedItem.dataset.blockindex),
        categoryIndex: parseInt(droppedItem.dataset.categoryindex),
      });
    } else if (evt.from.id == "saved-panel-blocks") {
      // Handle drops from the new SavedBlocksPanel
      const blockId = parseInt(droppedItem.dataset.blockid);
      const isSynced = droppedItem.dataset.synced === "true";
      window.$event.emit("addSavedBlockFromPanel", {
        blockId,
        isSynced,
        sectionIndex: evt.newIndex,
      });
    } else {
      const cols = droppedItem.dataset.cols;
      const resolvedColumn = resolve(cols);
      // droppedItem.parentNode.removeChild(droppedItem);

      mjmlStore.addSection({
        sectionIndex: evt.newIndex,
        columns: resolvedColumn.structure,
        type: resolvedColumn.type,
      });
    }
  }

  function onPaste(e: ClipboardEvent) {
    if (!e.clipboardData || window.getSelection()?.toString() !== "") {
      return;
    }
    const data = getCopiedElement(e.clipboardData.getData("text"));

    if ((mjmlStore.selectedBlock || mjmlStore.selectedSection) && data) {
      if (
        Array.isArray(data) &&
        data.every((s) => s.tagName === "mj-section") &&
        mjmlStore.selectedSection
      ) {
        for (let i = data.length - 1; i >= 0; i--) {
          const s = data[i];
          mjmlStore.addCopiedSection({ direction: "bottom", section: s });
        }
        return;
      }
      if (!("tagName" in data)) {
        return;
      }
      if (data.tagName === "mj-section" && mjmlStore.selectedSection) {
        mjmlStore.addCopiedSection({ direction: "bottom", section: data });
        e.stopPropagation();
        return;
      }
      if (data.tagName !== "mj-section" && mjmlStore.selectedBlock) {
        mjmlStore.addCopiedBlock({ direction: "bottom", block: data });
        e.stopPropagation();
      }
    }
  }

  function firefoxGlobalPaste(e: ClipboardEvent) {
    const firefoxOriginalTarget = (e as any).explicitOriginalTarget;
    if (firefoxOriginalTarget && firefoxOriginalTarget.closest(".email")) {
      onPaste(e);
    }
  }

  onMounted(() => {
    document.addEventListener("paste", firefoxGlobalPaste);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("paste", firefoxGlobalPaste);
  });
</script>

<style scoped lang="scss">
  .mobilePreview {
    display: flex;
    justify-content: center;
    width: 407px !important;
  }

  .mobilePreviewWrapper {
    width: 100%;
    display: flex;
    justify-content: center;
  }
</style>
